import { useState, useMemo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

// material-ui
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { alpha, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

// ant design icons
import DisconnectOutlined from '@ant-design/icons/DisconnectOutlined';
import PlusOutlined from '@ant-design/icons/PlusOutlined';
import MinusOutlined from '@ant-design/icons/MinusOutlined';
import SaveOutlined from '@ant-design/icons/SaveOutlined';
import UserOutlined from '@ant-design/icons/UserOutlined';
import TeamOutlined from '@ant-design/icons/TeamOutlined';
import DownloadOutlined from '@ant-design/icons/DownloadOutlined';
import DeleteOutlined from '@ant-design/icons/DeleteOutlined';
import EyeOutlined from '@ant-design/icons/EyeOutlined';
import EditOutlined from '@ant-design/icons/EditOutlined';
import InfoCircleOutlined from '@ant-design/icons/InfoCircleOutlined';

// third party
import { format } from 'date-fns';
import { jsPDF } from 'jspdf';
import { DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, rectSortingStrategy, useSortable } from '@dnd-kit/sortable';

// project imports
import MainCard from 'components/MainCard';
import { HistoryEmptyState } from 'components/HistoryPagination';
import useDatasheetHistory, { historyDateKey } from 'hooks/useDatasheetHistory';
import { CSVExport, DebouncedInput, TablePagination } from 'components/third-party/react-table';
import { openSnackbar } from 'api/snackbar';
import useOnlineStatus from 'hooks/useOnlineStatus';
import { getMaterialsMetadata, updateMaterialMetadata } from 'services/api/materials';

// ant design icons for offline
import LeftOutlined from '@ant-design/icons/LeftOutlined';
import SpeechTextField from 'components/SpeechTextField';

const EMPTY_ARRAY = [];

// Custom mands are ad-hoc mands a therapist logs mid-session that aren't tied to
// any IEP target. They live ONLY in this session's data sheet (persisted under
// raw_data.customMands) and never fold into the programme summary
// (promptedMands / independentMands) that feeds mand analytics — see handleSave.
// Their visual accent (coloured left border + faint card tint, so they read as
// distinct from target-driven mands) is derived from the Ayro palette primary
// token in-component — see customMandBorder / customMandTint below.

// Sentinel value for the "All Targets" dropdown option — chosen to never
// collide with a real target id (which are either GUIDs or short codes).
const ALL_TARGETS_VALUE = '__all_targets__';

// Read the session start timestamp written by data-collection.jsx into
// sessionStorage so RPM (responses-per-minute) can be calculated against the
// actual session duration, not a manually-entered value.
const SESSION_TIMER_STORAGE_PREFIX = 'dataCollection:sessionStart:';
const readSessionStart = (sessionKey) => {
  try {
    const v = Number(sessionStorage.getItem(`${SESSION_TIMER_STORAGE_PREFIX}${sessionKey || 'no-session'}`));
    return v && !Number.isNaN(v) ? v : null;
  } catch {
    return null;
  }
};

// A draggable, selectable material card for the Download dialog. Drag to
// reorder (the PDF follows this order); click (without dragging) toggles
// selection. The PointerSensor's activation distance keeps clicks from
// being mistaken for drags.
function SortableMaterialCard({ row, checked, onToggle, onEdit, disabled, showTarget }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: row.key, disabled });
  const style = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    transition,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 2 : 'auto'
  };
  return (
    <Grid size={{ xs: 6, sm: 4, md: 3 }} ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <Paper
        onClick={() => !disabled && onToggle(row.key)}
        sx={{
          p: 1,
          position: 'relative',
          cursor: disabled ? 'default' : 'grab',
          // Let the browser handle vertical scrolling; the touch sensor's long-press
          // delay is what starts a reorder, so we must NOT block native scroll here.
          // 'none' would capture every touch-move as a drag, so scrolling vertically
          // over a card would swap it instead of scrolling the grid.
          touchAction: 'pan-y',
          border: '2px solid',
          borderColor: checked ? 'primary.main' : 'grey.300',
          borderRadius: 2,
          transition: 'border-color 0.2s ease-in-out'
        }}
      >
        <Checkbox
          size="small"
          checked={checked}
          onChange={() => onToggle(row.key)}
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          disabled={disabled}
          sx={{ position: 'absolute', top: 2, right: 2, p: 0.5, bgcolor: 'background.paper', borderRadius: 1, zIndex: 1 }}
        />
        <Box
          component="img"
          src={row.material.url}
          alt={row.materialName}
          draggable={false}
          sx={{ width: '100%', height: 110, objectFit: 'contain', borderRadius: 1, display: 'block' }}
        />
        <Typography
          variant="caption"
          noWrap
          title={row.materialName}
          sx={{ display: 'block', textAlign: 'center', mt: 0.5, fontWeight: 600 }}
        >
          {row.materialName || row.material.name}
        </Typography>
        {showTarget && (
          <Typography variant="caption" color="text.secondary" noWrap title={row.targetName} sx={{ display: 'block', textAlign: 'center' }}>
            {row.targetName}
          </Typography>
        )}
        {/* Edit description & tags (image/name not editable). Stop propagation so it doesn't
            toggle selection or start a drag. Padded for a comfortable tap target. */}
        <Box sx={{ textAlign: 'center', mt: 0.25 }}>
          <Button
            variant="text"
            size="small"
            startIcon={<EditOutlined style={{ fontSize: '0.85rem' }} />}
            onClick={(e) => {
              e.stopPropagation();
              onEdit?.(row);
            }}
            onPointerDown={(e) => e.stopPropagation()}
            disabled={disabled}
            sx={{ px: 1.5, py: 0.5, fontSize: '0.75rem', textTransform: 'none', lineHeight: 1.2, borderRadius: 1 }}
          >
            Edit
          </Button>
        </Box>
      </Paper>
    </Grid>
  );
}

SortableMaterialCard.propTypes = {
  row: PropTypes.object.isRequired,
  checked: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
  onEdit: PropTypes.func,
  disabled: PropTypes.bool,
  showTarget: PropTypes.bool
};

const DailyMandMode = ({
  viewMode = 'individual',
  onDataChange,
  initialData,
  mandItems = EMPTY_ARRAY, // kept for backwards-compat; Quick Add now reads target.materials
  targets = EMPTY_ARRAY,
  // Offline mode props
  isOfflineMode = false,
  childId,
  childName,
  therapistId,
  therapistName,
  sessionId,
  onSave,
  onBack
}) => {
  const theme = useTheme();
  // Custom-mand accent, derived from the Ayro palette primary token (no hardcoded
  // hex): a solid left border + a faint same-hue wash. Kept identical in
  // DailyMandDetail so custom mands look the same wherever they appear.
  const customMandBorder = theme.palette.primary.main;
  const customMandTint = alpha(theme.palette.primary.main, 0.04);
  // Touch devices (iPad/tablets/phones): an <iframe> of a PDF only renders the FIRST page
  // with no scroll on iOS/iPadOS, so the preview would hide every card past page one.
  // For touch we render an in-app scrollable HTML page-preview instead; desktop keeps
  // the native PDF iframe (which paginates correctly).
  // Use ANY-pointer, not pointer: an iPad with a trackpad/keyboard reports its PRIMARY
  // pointer as `fine`, so `(pointer: coarse)` would be false and wrongly fall back to the
  // broken iframe. `(any-pointer: coarse)` is true whenever a touchscreen exists.
  const isTouch = useMediaQuery('(any-pointer: coarse)');
  const intl = useIntl();

  // Get actual network status - auto-detect offline regardless of isOfflineMode prop
  const { isOnline } = useOnlineStatus();

  // Only surface targets that are currently being worked on: dateIntroduced
  // set and on/before today, dateMastered still empty. Targets introduced in
  // the future or already mastered are filtered out of every view (dropdown,
  // Quick Add, grid, Download All).
  const activeTargets = useMemo(() => {
    const todayEnd = new Date();
    todayEnd.setHours(23, 59, 59, 999);
    return (targets || []).filter((t) => {
      if (typeof t !== 'object' || !t) return false;
      if (!t.dateIntroduced) return false;
      const intro = new Date(t.dateIntroduced);
      if (Number.isNaN(intro.getTime())) return false;
      if (intro.getTime() > todayEnd.getTime()) return false;
      if (t.dateMastered) return false;
      return true;
    });
  }, [targets]);

  const [mandOccurrences, setMandOccurrences] = useState([]);
  const [selectedTarget, setSelectedTarget] = useState(ALL_TARGETS_VALUE);
  const [tabValue, setTabValue] = useState(0);
  const [globalFilter, setGlobalFilter] = useState('');
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  // Per (target × material) count store — keyed by `${targetId}::${materialKey}`.
  // Used by BOTH views so individual ↔ grid stays in sync.
  const [gridPromptedCounts, setGridPromptedCounts] = useState({});
  const [gridIndependentCounts, setGridIndependentCounts] = useState({});
  // Custom (ad-hoc) mands added during the session. Each: { id, name, prompted,
  // independent }. Not tied to any target; restored from initialData so reopening
  // the sheet keeps whatever was added.
  const [customMands, setCustomMands] = useState(() =>
    Array.isArray(initialData?.customMands) ? initialData.customMands.map((m) => ({ ...m })) : []
  );
  const [imagePopup, setImagePopup] = useState({ open: false, item: null });
  const [sessionNotes, setSessionNotes] = useState('');
  const [saving, setSaving] = useState(false);
  // "Download All" now opens a picker dialog: therapist selects which material
  // images to export, then they're rendered into a single PDF (one image/page).
  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);
  const [selectedDownloadKeys, setSelectedDownloadKeys] = useState(() => new Set());
  const [generatingPdf, setGeneratingPdf] = useState(false);
  // PDF page layout: N images per row/column (1 = 1×1 = one image/page,
  // 2 = 2×2 = four/page, 3 = 3×3 = nine/page).
  const [pdfLayout, setPdfLayout] = useState(1);
  // "Beside Image" mode lays out full-width rows (image left, name right); this many per page.
  const BESIDE_ROWS = 4;
  // Title options for the exported PDF.
  // showTitle: print the material name at all. titlePosition: 'below' the image (caption), or
  // 'foldable' (image top half / title bottom half with a fold line — restricted to the 2×2
  // layout). titleFontSize maps to Heading 1/2/3.
  const [showTitle, setShowTitle] = useState(true);
  const [titlePosition, setTitlePosition] = useState('below');
  const [titleFontSize, setTitleFontSize] = useState('medium');
  // Pre-download preview (blob URL of the generated PDF shown in an iframe).
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  // Manual ordering of the download cards (array of row keys). Drag to reorder;
  // the PDF is built in this order. Synced to downloadableRows below.
  const [downloadOrder, setDownloadOrder] = useState([]);
  // Mouse: drag starts after an 8px move (desktop stays click-to-select, drag-to-reorder).
  // Touch: drag requires a long-press (250ms hold). A finger that moves before the delay
  // is treated as a scroll and cancels activation, so scrolling the grid never reorders —
  // reordering only begins after the user presses and holds a card, then drags it.
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } })
  );
  // Map of file path (or filename) → { name, category, ... } from the
  // /materials/metadata endpoint. Used to resolve material display names
  // ("Daddy") for entries that still have the raw filename ("father.jpg")
  // stored on the IEP target.
  const [materialMetadata, setMaterialMetadata] = useState({});
  useEffect(() => {
    let cancelled = false;
    getMaterialsMetadata()
      .then((res) => {
        if (!cancelled) setMaterialMetadata(res?.metadata || {});
      })
      .catch(() => {
        if (!cancelled) setMaterialMetadata({});
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Resolve a material's display name. Priority:
  //   1. Metadata-provided name (supervisor renamed it in Material Repository)
  //   2. Stripped/beautified filename (covers child-specific materials
  //      whose filenames already encode the display name + extension)
  // Falls through to '-' so the card never renders an empty title.
  const materialDisplayName = (m) => {
    if (!m) return '';
    const meta = materialMetadata[m.path] || materialMetadata[m.name] || {};
    if (meta.name) return meta.name;
    const raw = m.name || m.path || '';
    if (!raw) return '';
    const noExt = String(raw).replace(/\.[^/.]+$/, '');
    return noExt.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  };

  // ---- Edit material (description + tags only) from the Download dialog ----------------------
  const [editMatRow, setEditMatRow] = useState(null); // the row being edited, or null
  const [editDesc, setEditDesc] = useState('');
  const [editTags, setEditTags] = useState([]);
  const [savingMeta, setSavingMeta] = useState(false);

  // The metadata key for a material is its file path (fallback to filename).
  const metaKeyFor = (m) => m?.path || m?.name || '';
  // Trim, drop blanks, de-dupe (case-insensitive), cap at 5.
  const normalizeMetaTags = (list) => {
    const out = [];
    (list || []).forEach((raw) => {
      const tag = (raw || '').trim();
      if (!tag || out.some((t) => t.toLowerCase() === tag.toLowerCase())) return;
      if (out.length < 5) out.push(tag);
    });
    return out;
  };

  const openEditMaterial = async (row) => {
    setEditMatRow(row);
    // Seed from whatever metadata we already have so the fields aren't blank for a moment.
    const seed = materialMetadata[row.material.path] || materialMetadata[row.material.name] || {};
    setEditDesc(seed.description || '');
    setEditTags(Array.isArray(seed.tags) ? seed.tags : []);
    // Then refresh from the server so the LATEST saved description/tags show (the mount-time
    // snapshot can be stale if the material was edited elsewhere after this page loaded).
    try {
      const res = await getMaterialsMetadata();
      const all = res?.metadata || {};
      setMaterialMetadata(all);
      const fresh = all[row.material.path] || all[row.material.name] || {};
      setEditDesc(fresh.description || '');
      setEditTags(Array.isArray(fresh.tags) ? fresh.tags : []);
    } catch {
      /* keep the seeded values on failure */
    }
  };

  const closeEditMaterial = () => {
    if (!savingMeta) setEditMatRow(null);
  };

  const saveEditMaterial = async () => {
    if (!editMatRow) return;
    const m = editMatRow.material;
    const key = metaKeyFor(m);
    const existing = materialMetadata[m.path] || materialMetadata[m.name] || {};
    const tags = normalizeMetaTags(editTags);
    // Keep name & category as-is (not editable here); only description & tags change.
    const payload = {
      filename: key,
      name: existing.name || materialDisplayName(m),
      category: existing.category || 'Other',
      description: editDesc.trim(),
      tags
    };
    setSavingMeta(true);
    try {
      await updateMaterialMetadata(key, payload);
      // Reflect immediately so the card/name update without a reload.
      setMaterialMetadata((prev) => ({ ...prev, [key]: { ...existing, ...payload } }));
      openSnackbar({
        open: true,
        message: intl.formatMessage({ id: 'form.dailyMand.materialUpdated', defaultMessage: 'Material updated.' }),
        variant: 'alert',
        alert: { color: 'success' }
      });
      setEditMatRow(null);
    } catch {
      openSnackbar({
        open: true,
        message: intl.formatMessage({ id: 'form.dailyMand.materialUpdateFailed', defaultMessage: 'Could not update the material.' }),
        variant: 'alert',
        alert: { color: 'error' }
      });
    } finally {
      setSavingMeta(false);
    }
  };

  // Tick once per second so RPM (Total Mands / Session Duration) updates live.
  const [sessionElapsedSec, setSessionElapsedSec] = useState(() => {
    const start = readSessionStart(sessionId);
    return start ? Math.floor((Date.now() - start) / 1000) : 0;
  });
  useEffect(() => {
    const tick = () => {
      const start = readSessionStart(sessionId);
      setSessionElapsedSec(start ? Math.floor((Date.now() - start) / 1000) : 0);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [sessionId]);

  // Ref to skip auto-save on initial render
  const initialRenderRef = useRef(true);

  // Auto-save: notify parent when form data changes so it can debounce and persist
  useEffect(() => {
    if (initialRenderRef.current) {
      initialRenderRef.current = false;
      return;
    }

    // Skip if no data has been entered
    const hasOccurrences = mandOccurrences.length > 0;
    const hasGridData = Object.values(gridPromptedCounts).some((v) => v > 0) || Object.values(gridIndependentCounts).some((v) => v > 0);
    const persistedCustomMands = getPersistableCustomMands();
    if (!hasOccurrences && !hasGridData && persistedCustomMands.length === 0) return;

    const formData = {
      selectedTarget,
      rpm: getRpm(),
      sessionDurationSec: sessionElapsedSec,
      promptedMands: getGridPromptedTotal(),
      independentMands: getGridIndependentTotal(),
      totalMands: getGridTotalMands(),
      occurrences: mandOccurrences,
      gridPromptedCounts,
      gridIndependentCounts,
      // Session-only ad-hoc mands. Kept out of the programme summary above so
      // they never affect target-level mand analytics.
      customMands: persistedCustomMands,
      sessionNotes
    };

    if (onDataChange) onDataChange('daily-mand', formData);
  }, [mandOccurrences, gridPromptedCounts, gridIndependentCounts, customMands, selectedTarget, sessionNotes]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle save - uses offline-first pattern (always saves to IndexedDB, syncs in background)
  const handleSave = async (showNotification = true) => {
    setSaving(true);
    try {
      // Don't save if no data (target mands OR ad-hoc custom mands)
      const persistedCustomMands = getPersistableCustomMands();
      if (getGridTotalMands() === 0 && persistedCustomMands.length === 0) {
        setSaving(false);
        return;
      }

      const formData = {
        selectedTarget,
        rpm: getRpm(),
        sessionDurationSec: sessionElapsedSec,
        promptedMands: getGridPromptedTotal(),
        independentMands: getGridIndependentTotal(),
        totalMands: getGridTotalMands(),
        occurrences: mandOccurrences,
        gridPromptedCounts,
        gridIndependentCounts,
        // Session-only ad-hoc mands; excluded from the programme summary above.
        customMands: persistedCustomMands,
        sessionNotes
      };

      // Notify parent to persist via its upsert pattern (persistToIndexedDB)
      if (onDataChange) {
        onDataChange('daily-mand', formData);
      }

      if (showNotification) {
        openSnackbar({
          open: true,
          message: isOnline
            ? intl.formatMessage({ id: 'form.msg.dataSaved' }, { formName: intl.formatMessage({ id: 'form.dailyMand.title' }) })
            : intl.formatMessage({ id: 'form.msg.dataSavedOffline' }, { formName: intl.formatMessage({ id: 'form.dailyMand.title' }) }),
          variant: 'alert',
          alert: { color: 'success' }
        });
      }

      if (onSave) onSave();
      if (isOfflineMode && onBack) onBack();
    } catch (error) {
      console.error('Error saving entry:', error);
      if (showNotification) {
        openSnackbar({
          open: true,
          message: intl.formatMessage({ id: 'form.msg.saveFailed' }),
          variant: 'alert',
          alert: { color: 'error' }
        });
      }
    } finally {
      setSaving(false);
    }
  };

  // Alias for backward compatibility with existing UI buttons
  const handleOfflineSave = handleSave;

  const handleCloseImagePopup = () => {
    setImagePopup({ open: false, item: null });
  };

  // Default to "All Targets" when targets load (or change). Therapists can
  // narrow to a single target via the dropdown.
  useEffect(() => {
    setSelectedTarget(ALL_TARGETS_VALUE);
  }, [targets]);

  /* PREVIOUS SESSIONS. This list used to be local state, initialised empty and reset
   * whenever the target list changed — nothing ever put a row in it, so the
   * Historical tab was permanently blank. It is derived from the saved entries
   * instead, one row per submitted session.
   *
   * Range-bounded rather than paged to five: this tab has its own date filters, text
   * search and TablePagination, all of which run across the whole list — a
   * five-record page would make "search" mean "search five records". */
  const historyWindow = useMemo(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 30);
    const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return { start: iso(start), end: iso(end) };
  }, []);

  const { records: historyRecords, isEmpty: historyIsEmpty } = useDatasheetHistory(childId, 'daily_mand', {
    startDate: historyWindow.start,
    endDate: historyWindow.end
  });

  const allTargetsLabel = intl.formatMessage({ id: 'form.dailyMand.allTargets' });

  // Grid counts are keyed by IEP target id, so the target list names them.
  const targetNameById = useMemo(() => {
    const map = {};
    (targets || []).forEach((t) => {
      if (t?.id) map[t.id] = t.name || t.description || '';
    });
    return map;
  }, [targets]);

  const historicalData = useMemo(
    () =>
      historyRecords
        .map((record) => {
          const rd = record.raw_data || {};
          const date = historyDateKey(record);
          if (!date) return null;
          /* Which mands were used: the per-target grid tallies plus any mands added by
           * name. Ids that no longer resolve to a target are dropped rather than
           * printed raw — a bare UUID in a history column is noise. */
          const usedIds = new Set([...Object.keys(rd.gridIndependentCounts || {}), ...Object.keys(rd.gridPromptedCounts || {})]);
          const names = [...usedIds].map((id) => targetNameById[id]).filter(Boolean);
          (rd.customMands || []).forEach((m) => {
            if (m?.name && (m.independent || m.prompted)) names.push(m.name);
          });
          const unnamed = usedIds.size - names.length;
          return {
            date,
            target: rd.selectedTarget && rd.selectedTarget !== ALL_TARGETS_VALUE ? rd.selectedTarget : allTargetsLabel,
            promptedMands: rd.promptedMands ?? 0,
            independentMands: rd.independentMands ?? 0,
            itemsUsed: names.length > 0 ? names.join(', ') : unnamed > 0 ? `${unnamed}` : '—',
            rpm: rd.rpm ?? '0.00'
          };
        })
        .filter(Boolean),
    [historyRecords, targetNameById, allTargetsLabel]
  );

  // Compose a stable key for a (target, material) pair. Counts and recent-mand
  // occurrences are keyed off this so individual ↔ grid views stay in sync and
  // therapists can use the same material across multiple targets without
  // collisions.
  const matKey = (targetId, m) => `${targetId}::${m?.source || 'unknown'}::${m?.path || m?.name || ''}`;

  // Fetch a material image and return it as a base64 data URL. The backend
  // serves materials with `Content-Disposition: inline`, so we read the bytes
  // ourselves rather than relying on the response headers. Used to embed
  // images into the generated PDF.
  const fetchImageAsDataUrl = async (url) => {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  // Read natural dimensions of a data-URL image so each PDF page can scale the
  // image to fit while preserving aspect ratio.
  const readImageSize = (dataUrl) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve({ width: img.naturalWidth || 1, height: img.naturalHeight || 1 });
      img.onerror = reject;
      img.src = dataUrl;
    });

  // Map a data-URL MIME type to a jsPDF-supported format string. jsPDF only
  // accepts JPEG/PNG/WEBP rasters; anything else is treated as JPEG.
  const imageFormatFromDataUrl = (dataUrl) => {
    const mime = dataUrl.substring(dataUrl.indexOf(':') + 1, dataUrl.indexOf(';')).toLowerCase();
    if (mime.includes('png')) return 'PNG';
    if (mime.includes('webp')) return 'WEBP';
    return 'JPEG';
  };

  const toggleDownloadKey = (key) => {
    setSelectedDownloadKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Render the selected material images into a single PDF arranged in an
  // `layout` × `layout` grid per A4 page (1 = one image/page, 2 = four/page,
  // 3 = nine/page), each cell showing the image with its material name below.
  // Returns the jsPDF doc, or null if nothing could be rendered.
  const buildMaterialsPdf = async (layout) => {
    // Respect the manual drag order, keeping only selected rows.
    const rows = orderedDownloadRows.filter((r) => selectedDownloadKeys.has(r.key));
    if (rows.length === 0) return null;

    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 40;
    // "Beside Image": full-width rows (image left, name right, vertical fold). Ignores the
    // 1×1/2×2/3×3 grid and uses BESIDE_ROWS full-width rows per page.
    const besideMode = showTitle && titlePosition === 'beside';
    const cols = besideMode ? 1 : layout;
    const rowsPerPage = besideMode ? BESIDE_ROWS : layout;
    const perPage = cols * rowsPerPage;
    // No gap between cells — cards sit edge-to-edge so their borders merge into one
    // continuous grid (clean cut lines, like a printed worksheet), with no whitespace gaps.
    const gap = 0;
    const cellW = (pageW - margin * 2 - gap * (cols - 1)) / cols;
    const cellH = (pageH - margin * 2 - gap * (rowsPerPage - 1)) / rowsPerPage;

    // Title options (from the dialog). Font sizes map Large/Medium/Small -> Heading 1/2/3.
    const titlePt = { large: 24, medium: 18, small: 14 }[titleFontSize] || 18;
    // "Below (Foldable)" splits each tile in half — image on top (full width), title in the
    // bottom half below a horizontal fold line (restricted to the 2×2 layout in the UI).
    // Otherwise the title sits in a small caption band below the image.
    const foldable = showTitle && titlePosition === 'foldable';
    // Bottom padding so the title isn't glued to the cell border.
    const TITLE_BOTTOM_PAD = 20;
    const captionH = showTitle && !foldable && !besideMode ? titlePt + TITLE_BOTTOM_PAD + 8 : 0;
    const BESIDE_SPLIT = 0.5; // fold at the exact middle: image in the left half, name in the right
    const CELL_PAD = 6;
    let added = 0;

    // Draw the layout grid on the current page so the sheet can be cut. A SOLID outer frame marks
    // the sheet boundary; DASHED interior dividers are the "cut here" guides. Drawn AFTER the
    // images (see below) so an image's white background can never paint over the lines.
    // 1×1 → just the solid frame; 2×2 → frame + 1 dashed cross; 3×3 → frame + dashed 9-grid.
    const drawPageGrid = () => {
      const gridW = cols * cellW; // == content width (gap is 0)
      const gridH = rowsPerPage * cellH;
      doc.setDrawColor(120);

      // Solid outer frame
      doc.setLineDashPattern([], 0);
      doc.setLineWidth(0.9);
      doc.rect(margin, margin, gridW, gridH);

      // Dashed interior cut dividers
      doc.setLineWidth(0.6);
      doc.setLineDashPattern([2, 2], 0);
      for (let c = 1; c < cols; c += 1) {
        const x = margin + c * cellW;
        doc.line(x, margin, x, margin + gridH);
      }
      for (let r = 1; r < rowsPerPage; r += 1) {
        const y = margin + r * cellH;
        doc.line(margin, y, margin + gridW, y);
      }

      // Foldable: a fold line down the middle of each tile — longer dashes so it reads as
      // "fold" rather than "cut".
      if (foldable || besideMode) {
        doc.setLineDashPattern([6, 3], 0);
        for (let i = 0; i < perPage; i += 1) {
          const c = i % cols;
          const r = Math.floor(i / cols);
          const cx = margin + c * cellW;
          const cy = margin + r * cellH;
          if (besideMode) {
            // vertical fold between the image (left) and the name (right)
            const fx = cx + cellW * BESIDE_SPLIT;
            doc.line(fx, cy, fx, cy + cellH);
          } else {
            // horizontal fold between the image (top) and the name (bottom)
            doc.line(cx, cy + cellH / 2, cx + cellW, cy + cellH / 2);
          }
        }
      }

      doc.setLineDashPattern([], 0); // reset for any later drawing
    };

    for (const row of rows) {
      try {
        const dataUrl = await fetchImageAsDataUrl(row.material.url);
        const { width, height } = await readImageSize(dataUrl);

        const cellIndex = added % perPage;
        if (cellIndex === 0 && added > 0) doc.addPage();
        const colI = cellIndex % cols;
        const rowI = Math.floor(cellIndex / cols);
        const cellX = margin + colI * (cellW + gap);
        const cellY = margin + rowI * (cellH + gap);

        // Image area:
        //  - beside: the left BESIDE_SPLIT of the row (name goes on the right)
        //  - foldable: the top half (name below the horizontal fold)
        //  - below/none: full cell width minus the caption band
        const imgAreaW = besideMode ? cellW * BESIDE_SPLIT : cellW;
        const imgAreaH = foldable ? cellH / 2 : cellH - captionH;
        const maxW = imgAreaW - CELL_PAD * 2;
        const maxH = imgAreaH - CELL_PAD * 2;
        const ratio = Math.min(maxW / width, maxH / height);
        const w = width * ratio;
        const h = height * ratio;
        const x = cellX + (imgAreaW - w) / 2;
        const y = cellY + (imgAreaH - h) / 2;
        doc.addImage(dataUrl, imageFormatFromDataUrl(dataUrl), x, y, w, h);

        if (showTitle) {
          const titleText = row.materialName || row.material.name || '';
          doc.setFontSize(titlePt);
          if (besideMode) {
            // Name centered in the right portion of the row, vertically centered
            const nameX = cellX + cellW * BESIDE_SPLIT + (cellW * (1 - BESIDE_SPLIT)) / 2;
            doc.text(titleText, nameX, cellY + cellH / 2, {
              align: 'center',
              baseline: 'middle',
              maxWidth: cellW * (1 - BESIDE_SPLIT) - CELL_PAD * 2
            });
          } else if (foldable) {
            // Centered in the bottom half, below the horizontal fold line
            doc.text(titleText, cellX + cellW / 2, cellY + cellH * 0.75, {
              align: 'center',
              baseline: 'middle',
              maxWidth: cellW - CELL_PAD * 2
            });
          } else {
            // Centered in the caption band below the image, with bottom padding off the border
            doc.text(titleText, cellX + cellW / 2, cellY + cellH - TITLE_BOTTOM_PAD, {
              align: 'center',
              maxWidth: cellW - CELL_PAD * 2
            });
          }
        }
        added += 1;
      } catch (err) {
        console.error('Skipping material in PDF export', row?.material?.url, err);
      }
    }

    // Draw the grid on every page last, so the cut lines sit on top of the images and the
    // outer frame is always complete (no image background painting over the borders).
    const totalPages = doc.getNumberOfPages();
    for (let p = 1; p <= totalPages; p += 1) {
      doc.setPage(p);
      drawPageGrid();
    }

    return added > 0 ? doc : null;
  };

  const handleDownloadSelectedAsPdf = async () => {
    setGeneratingPdf(true);
    try {
      const doc = await buildMaterialsPdf(pdfLayout);
      if (!doc) {
        openSnackbar({
          open: true,
          message: intl.formatMessage({ id: 'form.msg.materialDownloadFailed' }),
          variant: 'alert',
          alert: { color: 'error' }
        });
        return;
      }
      doc.save(`daily-mand-materials-${format(new Date(), 'yyyy-MM-dd')}.pdf`);
      setDownloadDialogOpen(false);
    } finally {
      setGeneratingPdf(false);
    }
  };

  // Build the PDF and show it in an in-app preview (iframe) before downloading.
  const handlePreviewPdf = async () => {
    // Touch: skip the PDF iframe (iOS renders only page 1) and open the scrollable
    // HTML page-preview built from the current selection + layout options.
    if (isTouch) {
      if (previewPages.pages.length === 0) {
        openSnackbar({
          open: true,
          message: intl.formatMessage({ id: 'form.msg.materialDownloadFailed' }),
          variant: 'alert',
          alert: { color: 'error' }
        });
        return;
      }
      setPreviewOpen(true);
      return;
    }
    setGeneratingPdf(true);
    try {
      const doc = await buildMaterialsPdf(pdfLayout);
      if (!doc) {
        openSnackbar({
          open: true,
          message: intl.formatMessage({ id: 'form.msg.materialDownloadFailed' }),
          variant: 'alert',
          alert: { color: 'error' }
        });
        return;
      }
      const url = URL.createObjectURL(doc.output('blob'));
      setPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return url;
      });
      setPreviewOpen(true);
    } finally {
      setGeneratingPdf(false);
    }
  };

  // Download the exact PDF currently shown in the preview.
  const handleDownloadFromPreview = async () => {
    // Touch: the preview is an HTML page-preview, not a pre-built blob, so build the
    // PDF now (same layout options) and save it.
    if (isTouch || !previewUrl) {
      setGeneratingPdf(true);
      try {
        const doc = await buildMaterialsPdf(pdfLayout);
        if (!doc) {
          openSnackbar({
            open: true,
            message: intl.formatMessage({ id: 'form.msg.materialDownloadFailed' }),
            variant: 'alert',
            alert: { color: 'error' }
          });
          return;
        }
        doc.save(`daily-mand-materials-${format(new Date(), 'yyyy-MM-dd')}.pdf`);
        closePreview();
        setDownloadDialogOpen(false);
      } finally {
        setGeneratingPdf(false);
      }
      return;
    }
    const a = document.createElement('a');
    a.href = previewUrl;
    a.download = `daily-mand-materials-${format(new Date(), 'yyyy-MM-dd')}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    closePreview();
    setDownloadDialogOpen(false);
  };

  const closePreview = () => {
    setPreviewOpen(false);
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  };

  const getTargetId = (t) => (typeof t === 'object' && t !== null ? t.id : t);
  const getTargetName = (t) => (typeof t === 'object' && t !== null ? t.name || t.description || String(t.id ?? '') : String(t ?? ''));

  // When a target has no materials configured we still want a row for it so
  // therapists can record mands at the target level (1:1). Synthesize a
  // material whose name is the target name; downloads are disabled (no URL).
  const targetSelfMaterial = (t) => ({
    source: 'target',
    path: String(getTargetId(t) ?? ''),
    name: getTargetName(t),
    url: null
  });

  const materialsForTarget = (t) => {
    if (!t || typeof t !== 'object') return [];
    const mats = Array.isArray(t.materials) ? t.materials : [];
    return mats.length > 0 ? mats : [targetSelfMaterial(t)];
  };

  // One row PER TARGET (not per material). Mands are recorded at the target
  // level; the target's materials are carried along only so the "Download All"
  // picker can list their images. Used by the grid view, overall totals, and
  // the "All Targets" Quick Add mode.
  const flatRows = useMemo(() => {
    return activeTargets.map((t) => {
      const targetId = getTargetId(t);
      return {
        key: String(targetId),
        target: t,
        targetId,
        targetName: getTargetName(t),
        materials: materialsForTarget(t)
      };
    });
    // Recompute when metadata loads so material display names (used in the
    // download dialog) refresh once the metadata fetch resolves.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTargets, materialMetadata]);

  const isAllTargetsMode = selectedTarget === ALL_TARGETS_VALUE;

  // Rows rendered as Quick Add cards — one per target. Mirrors the grid view
  // when "All Targets" is selected; otherwise just the single chosen target.
  const quickAddRows = useMemo(() => {
    if (isAllTargetsMode) return flatRows;
    if (!selectedTarget || typeof selectedTarget !== 'object') return [];
    const targetId = getTargetId(selectedTarget);
    return [
      {
        key: String(targetId),
        target: selectedTarget,
        targetId,
        targetName: getTargetName(selectedTarget),
        materials: materialsForTarget(selectedTarget)
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAllTargetsMode, selectedTarget, flatRows, materialMetadata]);

  // Flatten every target's materials that have a real image URL into the list
  // shown in the Download All picker. Keyed per (target × material) so each
  // image can be individually selected; synthetic target-as-material rows
  // (no URL) are excluded.
  const downloadableRows = useMemo(() => {
    const out = [];
    quickAddRows.forEach((row) => {
      (row.materials || []).forEach((m) => {
        if (m?.url) {
          out.push({
            key: matKey(row.targetId, m),
            target: row.target,
            targetId: row.targetId,
            targetName: row.targetName,
            material: m,
            materialName: materialDisplayName(m)
          });
        }
      });
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quickAddRows, materialMetadata]);

  // Keep the manual order in sync with the available rows: preserve the current
  // sequence for keys that still exist, append any new keys, drop missing ones.
  useEffect(() => {
    const available = downloadableRows.map((r) => r.key);
    setDownloadOrder((prev) => {
      const kept = prev.filter((k) => available.includes(k));
      const added = available.filter((k) => !kept.includes(k));
      const next = [...kept, ...added];
      // Avoid a state update (and re-render loop) when nothing actually changed.
      return next.length === prev.length && next.every((k, i) => k === prev[i]) ? prev : next;
    });
  }, [downloadableRows]);

  // Rows in the user-chosen order (used for rendering AND the PDF sequence).
  const orderedDownloadRows = useMemo(() => {
    const byKey = new Map(downloadableRows.map((r) => [r.key, r]));
    return downloadOrder.map((k) => byKey.get(k)).filter(Boolean);
  }, [downloadOrder, downloadableRows]);

  // Chunk the selected images into pages using the SAME layout rules as the PDF
  // generator (buildMaterialsPdf), so the touch HTML preview matches the download.
  const previewPages = useMemo(() => {
    const selected = orderedDownloadRows.filter((r) => selectedDownloadKeys.has(r.key));
    const besideMode = showTitle && titlePosition === 'beside';
    const cols = besideMode ? 1 : pdfLayout;
    const rowsPerPage = besideMode ? BESIDE_ROWS : pdfLayout;
    const perPage = cols * rowsPerPage;
    const pages = [];
    for (let i = 0; i < selected.length; i += perPage) {
      pages.push(selected.slice(i, i + perPage));
    }
    return { pages, cols, besideMode };
  }, [orderedDownloadRows, selectedDownloadKeys, showTitle, titlePosition, pdfLayout]);

  const handleDownloadDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setDownloadOrder((prev) => {
      const from = prev.indexOf(active.id);
      const to = prev.indexOf(over.id);
      if (from === -1 || to === -1) return prev;
      return arrayMove(prev, from, to);
    });
  };

  const allDownloadSelected = downloadableRows.length > 0 && downloadableRows.every((r) => selectedDownloadKeys.has(r.key));
  const someDownloadSelected = downloadableRows.some((r) => selectedDownloadKeys.has(r.key));

  // Open the picker with every available image pre-selected (the common case
  // is "download them all"; deselecting a few is faster than selecting many).
  const handleOpenDownloadDialog = () => {
    setSelectedDownloadKeys(new Set(downloadableRows.map((r) => r.key)));
    setDownloadDialogOpen(true);
  };

  const handleCloseDownloadDialog = () => {
    if (generatingPdf) return;
    setDownloadDialogOpen(false);
  };

  const toggleSelectAllDownloads = () => {
    if (allDownloadSelected) setSelectedDownloadKeys(new Set());
    else setSelectedDownloadKeys(new Set(downloadableRows.map((r) => r.key)));
  };

  // ---- Custom (ad-hoc) mands ---------------------------------------------------------------
  const customMandTotal = (m) => (Number(m.prompted) || 0) + (Number(m.independent) || 0);

  // Only persist custom mands that carry something meaningful — a name or a count.
  // Empty add-cards (added but never filled in) are dropped so they don't clutter
  // the saved sheet or reappear on reopen.
  const getPersistableCustomMands = () => customMands.filter((m) => (m.name || '').trim() !== '' || customMandTotal(m) > 0);

  const addCustomMand = () => {
    setCustomMands((prev) => [
      ...prev,
      { id: `custom-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, name: '', prompted: 0, independent: 0 }
    ]);
  };

  const updateCustomMandName = (id, name) => {
    setCustomMands((prev) => prev.map((m) => (m.id === id ? { ...m, name } : m)));
  };

  /* WHEN A CUSTOM MAND WAS RECORDED, not just how many times.
   *
   * A custom mand used to be a pair of counters and nothing else, so View Data
   * could show "4 prompted" but never when any of them happened — the catalogue
   * mands drill down to per-occurrence times and the custom rows had nothing to
   * drill into. Each step of a counter now leaves an occurrence behind.
   *
   * Stored ON the custom mand rather than in the shared `occurrences` array: that
   * array is the programme summary, and anything in it is counted by the target
   * analytics and by View Data's header chips, which already add the custom
   * totals separately. Putting them there would double-count.
   *
   * Entries recorded before this exist with no `occurrences` key at all, which is
   * why the reader treats a missing array as "times unknown" rather than "none".
   */
  const buildCustomOccurrence = (type) => {
    const timestamp = new Date();
    return {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      type, // 'prompted' or 'independent'
      timestamp,
      time: timestamp.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    };
  };

  /* Bring a custom mand's occurrence list in line with a new count.
   *
   * Going up appends occurrences stamped now; going down drops the most recent
   * ones, so an accidental tap undone a second later does not leave a phantom
   * time behind. The grid's number input sets a count outright — typing "4"
   * cannot say when those four happened, so they are stamped at the moment the
   * therapist entered them, which is the only honest time available. */
  const reconcileCustomOccurrences = (mand, field, nextCount) => {
    const type = field === 'prompted' ? 'prompted' : 'independent';
    const existing = Array.isArray(mand.occurrences) ? mand.occurrences : [];
    const others = existing.filter((o) => o.type !== type);
    const mine = existing.filter((o) => o.type === type);
    if (nextCount > mine.length) {
      const added = Array.from({ length: nextCount - mine.length }, () => buildCustomOccurrence(type));
      return [...others, ...mine, ...added];
    }
    // Sorted oldest-first below, so trimming from the end drops the newest.
    return [...others, ...mine.slice(0, nextCount)];
  };

  // Step a counter by delta (+1 / -1) from the individual view's +/- buttons.
  // Floors at 0. Callers gate on a non-empty name, so no name check here.
  const adjustCustomMandCount = (id, type, delta) => {
    const field = type === 'prompted' ? 'prompted' : 'independent';
    setCustomMands((prev) =>
      prev.map((m) => {
        if (m.id !== id) return m;
        const next = Math.max(0, (Number(m[field]) || 0) + delta);
        return { ...m, [field]: next, occurrences: reconcileCustomOccurrences(m, field, next) };
      })
    );
  };

  // Set a counter directly from the grid view's number input.
  const setCustomMandCount = (id, type, rawValue) => {
    const field = type === 'prompted' ? 'prompted' : 'independent';
    const value = Math.max(0, parseInt(rawValue, 10) || 0);
    setCustomMands((prev) =>
      prev.map((m) => (m.id === id ? { ...m, [field]: value, occurrences: reconcileCustomOccurrences(m, field, value) } : m))
    );
  };

  const removeCustomMand = (id) => {
    setCustomMands((prev) => prev.filter((m) => m.id !== id));
  };

  const handleAddMand = (target, type) => {
    const targetId = getTargetId(target);
    const targetName = getTargetName(target);
    // Counts and occurrences are keyed PER TARGET now (one card per target).
    const key = String(targetId);
    const timestamp = new Date();
    const newOccurrence = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      key,
      targetId,
      targetName,
      // materialName mirrors the target so the View-Data drill-down groups by
      // target (mands are no longer recorded against individual materials).
      materialName: targetName,
      type, // 'prompted' or 'independent'
      timestamp,
      time: timestamp.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
      notes: ''
    };
    setMandOccurrences([newOccurrence, ...mandOccurrences]);

    if (type === 'prompted') {
      setGridPromptedCounts({ ...gridPromptedCounts, [key]: (gridPromptedCounts[key] || 0) + 1 });
    } else {
      setGridIndependentCounts({ ...gridIndependentCounts, [key]: (gridIndependentCounts[key] || 0) + 1 });
    }
  };

  // Parked handler — wired to the commented-out "Recent Mands" list. Kept in
  // place so re-enabling that UI is a single uncomment, per author intent.
  // eslint-disable-next-line no-unused-vars
  const handleRemoveMand = (id) => {
    const mandToRemove = mandOccurrences.find((m) => m.id === id);
    if (mandToRemove) {
      const key = mandToRemove.key;
      if (mandToRemove.type === 'prompted') {
        const newCount = Math.max(0, (gridPromptedCounts[key] || 0) - 1);
        setGridPromptedCounts({ ...gridPromptedCounts, [key]: newCount });
      } else {
        const newCount = Math.max(0, (gridIndependentCounts[key] || 0) - 1);
        setGridIndependentCounts({ ...gridIndependentCounts, [key]: newCount });
      }
    }
    setMandOccurrences(mandOccurrences.filter((m) => m.id !== id));
  };

  const handleRemoveMandByType = (target, type) => {
    const key = String(getTargetId(target));
    // Decrement the per-target count and drop the most recent matching
    // occurrence so the recent-mand log stays consistent.
    if (type === 'prompted') {
      if ((gridPromptedCounts[key] || 0) === 0) return;
      setGridPromptedCounts({ ...gridPromptedCounts, [key]: Math.max(0, (gridPromptedCounts[key] || 0) - 1) });
    } else {
      if ((gridIndependentCounts[key] || 0) === 0) return;
      setGridIndependentCounts({ ...gridIndependentCounts, [key]: Math.max(0, (gridIndependentCounts[key] || 0) - 1) });
    }
    const idx = mandOccurrences.findIndex((m) => m.key === key && m.type === type);
    if (idx >= 0) {
      const next = mandOccurrences.slice();
      next.splice(idx, 1);
      setMandOccurrences(next);
    }
  };

  // Parked — same status as handleRemoveMand above.
  // eslint-disable-next-line no-unused-vars
  const handleUpdateNotes = (id, notes) => {
    setMandOccurrences(mandOccurrences.map((m) => (m.id === id ? { ...m, notes } : m)));
  };

  const getCountByKey = (key, type) => {
    const src = type === 'prompted' ? gridPromptedCounts : gridIndependentCounts;
    return src[key] || 0;
  };

  const getGridPromptedTotal = () => {
    return Object.values(gridPromptedCounts).reduce((sum, val) => sum + (val || 0), 0);
  };

  const getGridIndependentTotal = () => {
    return Object.values(gridIndependentCounts).reduce((sum, val) => sum + (val || 0), 0);
  };

  const getGridTotalMands = () => {
    return getGridPromptedTotal() + getGridIndependentTotal();
  };

  // ---- What the OVERVIEW PANEL shows -------------------------------------
  // The grid totals above are the PROGRAMME summary: target-driven mands only,
  // and what gets persisted for mand analytics. The panel is a different
  // question - "what has been recorded this session" - and a custom mand counts
  // there, so these add them back in for display only. Keeping the two apart is
  // why a custom mand can be shown without ever reaching target analytics.
  const getCustomPromptedTotal = () => customMands.reduce((sum, m) => sum + (Number(m.prompted) || 0), 0);
  const getCustomIndependentTotal = () => customMands.reduce((sum, m) => sum + (Number(m.independent) || 0), 0);

  const getDisplayPromptedTotal = () => getGridPromptedTotal() + getCustomPromptedTotal();
  const getDisplayIndependentTotal = () => getGridIndependentTotal() + getCustomIndependentTotal();
  const getDisplayTotalMands = () => getDisplayPromptedTotal() + getDisplayIndependentTotal();

  // Total Mands / session duration (minutes). Recomputes each render — since
  // sessionElapsedSec ticks every second, the displayed RPM updates live.
  const getRpm = () => {
    // Responses per minute is a rate of what the child actually did, so an ad-hoc
    // mand counts towards it exactly as a target-driven one does.
    const total = getDisplayTotalMands();
    if (sessionElapsedSec <= 0 || total === 0) return '0.00';
    return (total / (sessionElapsedSec / 60)).toFixed(2);
  };

  // Filter historical data
  const filteredHistoricalData = useMemo(() => {
    let filtered = historicalData;

    // Filter by date range
    if (startDate || endDate) {
      filtered = filtered.filter((row) => {
        const rowDate = new Date(row.date);
        if (startDate && endDate) {
          return rowDate >= startDate && rowDate <= endDate;
        } else if (startDate) {
          return rowDate >= startDate;
        } else if (endDate) {
          return rowDate <= endDate;
        }
        return true;
      });
    }

    // Filter by search text
    if (globalFilter) {
      const lowerFilter = globalFilter.toLowerCase();
      /* `target` is a string for a whole-session row and the target OBJECT for a
       * single-target one — the same two shapes the table renders. Resolve to text
       * before matching, or searching throws on the object rows. */
      filtered = filtered.filter((row) => {
        const targetText =
          typeof row.target === 'object' && row.target !== null ? row.target.name || row.target.description || '' : row.target || '';
        return String(targetText).toLowerCase().includes(lowerFilter) || String(row.date || '').includes(lowerFilter);
      });
    }

    return filtered;
  }, [historicalData, globalFilter, startDate, endDate]);

  // Paginated data
  const paginatedData = useMemo(() => {
    const startIdx = pagination.pageIndex * pagination.pageSize;
    const endIdx = startIdx + pagination.pageSize;
    return filteredHistoricalData.slice(startIdx, endIdx);
  }, [filteredHistoricalData, pagination]);

  // Empty state — no MAND-domain IEP targets configured for this child
  if (activeTargets.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Alert severity="info">{intl.formatMessage({ id: 'form.msg.noMandItems' })}</Alert>
      </Box>
    );
  }

  // Individual View
  if (viewMode === 'individual') {
    // A single prompted/unprompted counter row for a custom mand card. Mirrors the
    // target-card counter row but is wired to the custom-mand state and stays
    // disabled until the mand has a name (per spec).
    const renderCustomCounterRow = (m, kind) => {
      const isPrompted = kind === 'prompted';
      const field = isPrompted ? 'prompted' : 'independent';
      const count = Number(m[field]) || 0;
      const color = isPrompted ? 'warning' : 'success';
      const label = intl.formatMessage({ id: isPrompted ? 'common.prompted' : 'form.dailyMand.unprompted' });
      const Icon = isPrompted ? TeamOutlined : UserOutlined;
      const disabled = !(m.name || '').trim();
      return (
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ width: '100%' }}>
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0, flex: 1 }}>
            <Icon style={{ fontSize: '0.95rem', color: theme.palette[color].main }} />
            <Typography
              variant="caption"
              noWrap
              sx={{ fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.4, color: `${color}.main` }}
            >
              {label}
            </Typography>
          </Stack>
          <ButtonGroup size="small" color={color} sx={{ flexShrink: 0 }}>
            <Button
              onClick={() => adjustCustomMandCount(m.id, field, -1)}
              disabled={disabled || count === 0}
              sx={{ px: 1, minWidth: 36 }}
              aria-label={`Decrease ${label}`}
            >
              <MinusOutlined style={{ fontSize: '0.8rem' }} />
            </Button>
            <Button
              disableRipple
              disabled={disabled}
              sx={{ minWidth: 48, px: 0.5, fontWeight: 700, fontSize: '0.95rem', cursor: 'default' }}
            >
              {count}
            </Button>
            <Button
              onClick={() => adjustCustomMandCount(m.id, field, 1)}
              disabled={disabled}
              sx={{ px: 1, minWidth: 36 }}
              aria-label={`Increase ${label}`}
            >
              <PlusOutlined style={{ fontSize: '0.8rem' }} />
            </Button>
          </ButtonGroup>
        </Stack>
      );
    };

    // A custom-mand card: coloured left border + faint tint to distinguish it from
    // target cards, a free-text (always editable) name, the running total in the
    // blue badge, and a delete control (custom mands only).
    const renderCustomMandCard = (m) => {
      const total = customMandTotal(m);
      return (
        <Grid size={{ xs: 12, sm: 6, md: 6, lg: 4 }} key={m.id}>
          <Paper
            sx={{
              p: 2,
              border: '1px solid',
              borderColor: 'grey.300',
              borderLeft: '4px solid',
              borderLeftColor: customMandBorder,
              bgcolor: customMandTint,
              borderRadius: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ minHeight: 36, borderBottom: '1px solid', borderColor: 'divider', pb: 1 }}
            >
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: 1,
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 700,
                  fontSize: '0.8rem',
                  flexShrink: 0
                }}
              >
                {total}
              </Box>
              <TextField
                size="small"
                fullWidth
                value={m.name}
                onChange={(e) => updateCustomMandName(m.id, e.target.value)}
                placeholder={intl.formatMessage({ id: 'form.dailyMand.customMandNamePlaceholder' })}
                inputProps={{ 'aria-label': 'Custom mand name', style: { textAlign: 'center', fontWeight: 600 } }}
                sx={{ flex: 1 }}
              />
              <Tooltip title={intl.formatMessage({ id: 'common.delete' })}>
                <IconButton size="small" onClick={() => removeCustomMand(m.id)} aria-label="Delete custom mand" sx={{ flexShrink: 0 }}>
                  <DeleteOutlined style={{ fontSize: '1rem' }} />
                </IconButton>
              </Tooltip>
            </Stack>
            <Stack spacing={1.25} sx={{ width: '100%' }}>
              {renderCustomCounterRow(m, 'prompted')}
              {renderCustomCounterRow(m, 'unprompted')}
            </Stack>
          </Paper>
        </Grid>
      );
    };

    // The "Add mand" card — stays in place so any number of custom mands can be
    // added. Keyboard-activatable for accessibility.
    const renderAddMandCard = () => (
      <Grid size={{ xs: 12, sm: 6, md: 6, lg: 4 }} key="__add_custom_mand__">
        <Paper
          role="button"
          tabIndex={0}
          onClick={addCustomMand}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              addCustomMand();
            }
          }}
          sx={{
            p: 2,
            height: '100%',
            minHeight: 140,
            border: '1px dashed',
            borderColor: 'grey.400',
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            cursor: 'pointer',
            transition: 'border-color 0.2s ease-in-out, background-color 0.2s ease-in-out',
            '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' }
          }}
        >
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: '50%',
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <PlusOutlined style={{ fontSize: '1.2rem' }} />
          </Box>
          <Typography variant="subtitle2" color="primary.main" fontWeight={600}>
            {intl.formatMessage({ id: 'form.dailyMand.addMand' })}
          </Typography>
        </Paper>
      </Grid>
    );

    return (
      <Box sx={isOfflineMode ? { p: { xs: 2, sm: 3 } } : {}}>
        {/* Network Lost Banner - shown when not in explicit offline mode but network is down */}
        {!isOfflineMode && !isOnline && (
          <Alert severity="info" icon={<DisconnectOutlined />} sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>{intl.formatMessage({ id: 'form.msg.offlineNote' })}</strong>
            </Typography>
          </Alert>
        )}

        {/* Offline Mode Header */}
        {isOfflineMode && (
          <MainCard sx={{ mb: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Button variant="text" startIcon={<LeftOutlined />} onClick={onBack}>
                  {intl.formatMessage({ id: 'form.button.back' })}
                </Button>
                <Box>
                  <Typography variant="h5">
                    {intl.formatMessage({ id: 'form.dailyMand.title' })} - {childName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {intl.formatMessage({ id: 'form.label.offlineMode' })}
                  </Typography>
                </Box>
              </Stack>
              <Button
                variant="contained"
                startIcon={<SaveOutlined />}
                onClick={handleOfflineSave}
                disabled={saving || (getGridTotalMands() === 0 && getPersistableCustomMands().length === 0)}
              >
                {saving ? intl.formatMessage({ id: 'form.button.saving' }) : intl.formatMessage({ id: 'form.button.saveOffline' })}
              </Button>
            </Stack>
          </MainCard>
        )}

        <Stack spacing={3}>
          {/* Summary Card — Mands Overview */}
          <MainCard>
            <Typography variant="h5" sx={{ mb: 3 }}>
              {intl.formatMessage({ id: 'form.dailyMand.mandsOverview' })}
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Stack spacing={0.5} alignItems="center">
                  <TeamOutlined style={{ fontSize: '2rem', color: theme.palette.warning.main }} />
                  <Typography variant="h3" fontWeight={700} color="warning.main">
                    {getDisplayPromptedTotal()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" textAlign="center">
                    {intl.formatMessage({ id: 'form.dailyMand.promptedMands' })}
                  </Typography>
                </Stack>
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Stack spacing={0.5} alignItems="center">
                  <UserOutlined style={{ fontSize: '2rem', color: theme.palette.success.main }} />
                  <Typography variant="h3" fontWeight={700} color="success.main">
                    {getDisplayIndependentTotal()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" textAlign="center">
                    {intl.formatMessage({ id: 'form.dailyMand.independentMands' })}
                  </Typography>
                </Stack>
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Stack spacing={0.5} alignItems="center">
                  <Typography variant="h3" fontWeight={700} color="info.main">
                    {getDisplayTotalMands()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" textAlign="center">
                    {intl.formatMessage({ id: 'form.dailyMand.totalMands' })}
                  </Typography>
                </Stack>
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Stack spacing={0.5} alignItems="center">
                  <Typography variant="h3" fontWeight={700} color="primary.main">
                    {getRpm()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" textAlign="center">
                    {intl.formatMessage({ id: 'form.dailyMand.responsesPerMin' })}
                  </Typography>
                </Stack>
              </Grid>
            </Grid>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mt: 3 }}>
              <FormControl fullWidth>
                <InputLabel>{intl.formatMessage({ id: 'form.dailyMand.relatedTarget' })}</InputLabel>
                <Select
                  value={
                    isAllTargetsMode
                      ? ALL_TARGETS_VALUE
                      : typeof selectedTarget === 'object'
                        ? (selectedTarget?.id ?? '')
                        : (selectedTarget ?? '')
                  }
                  label={intl.formatMessage({ id: 'form.dailyMand.relatedTarget' })}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === ALL_TARGETS_VALUE) {
                      setSelectedTarget(ALL_TARGETS_VALUE);
                      return;
                    }
                    const next = activeTargets.find((t) => (typeof t === 'object' ? t.id : t) === v) ?? v;
                    setSelectedTarget(next);
                  }}
                >
                  <MenuItem value={ALL_TARGETS_VALUE}>{intl.formatMessage({ id: 'form.dailyMand.allTargets' })}</MenuItem>
                  {activeTargets.map((target) => {
                    // Targets may arrive as strings (legacy) or as objects
                    // ({id, name, description, image}) when sourced from MAND-domain
                    // IEP targets. Render only the display label, never the object.
                    const value = typeof target === 'object' ? target.id : target;
                    const label = typeof target === 'object' ? target.name || target.description || String(target.id) : target;
                    return (
                      <MenuItem key={value} value={value}>
                        {label}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
              <Tooltip title={intl.formatMessage({ id: 'form.dailyMand.downloadAllMaterialsHelp' })}>
                <span>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadOutlined />}
                    onClick={handleOpenDownloadDialog}
                    disabled={downloadableRows.length === 0}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    {intl.formatMessage({ id: 'form.dailyMand.downloadAllMaterials' })}
                  </Button>
                </span>
              </Tooltip>
            </Stack>
          </MainCard>

          {/* Quick Add — one card per target. The card title is the target;
              its materials are not shown here (they're only listed in the
              Download All picker). Prompted/Unprompted counters record mands
              against the target. In "All Targets" mode every target renders. */}
          <MainCard>
            <Typography variant="h5" sx={{ mb: 3 }}>
              {intl.formatMessage({ id: 'form.dailyMand.quickAddMand' })}
            </Typography>
            <Box>
              {quickAddRows.length === 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {intl.formatMessage({ id: 'form.dailyMand.noMaterialsForTarget' })}
                </Alert>
              )}
              <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
                {quickAddRows.map((row) => {
                  const { key, target } = row;
                  const promptedCount = getCountByKey(key, 'prompted');
                  const independentCount = getCountByKey(key, 'independent');
                  const totalCount = promptedCount + independentCount;

                  const renderCounterRow = (kind) => {
                    const isPrompted = kind === 'prompted';
                    const countKey = isPrompted ? 'prompted' : 'independent';
                    const count = isPrompted ? promptedCount : independentCount;
                    const color = isPrompted ? 'warning' : 'success';
                    const label = intl.formatMessage({
                      id: isPrompted ? 'common.prompted' : 'form.dailyMand.unprompted'
                    });
                    const Icon = isPrompted ? TeamOutlined : UserOutlined;
                    // Horizontal row: label on the left, button group on the right.
                    // The card grid (lg: 4 → 3 cards per row) gives each card enough
                    // width that the label fits fully without clipping.
                    return (
                      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ width: '100%' }}>
                        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0, flex: 1 }}>
                          <Icon style={{ fontSize: '0.95rem', color: theme.palette[color].main }} />
                          <Typography
                            variant="caption"
                            noWrap
                            sx={{
                              fontWeight: 600,
                              fontSize: '0.7rem',
                              textTransform: 'uppercase',
                              letterSpacing: 0.4,
                              color: `${color}.main`
                            }}
                          >
                            {label}
                          </Typography>
                        </Stack>
                        <ButtonGroup size="small" color={color} sx={{ flexShrink: 0 }}>
                          <Tooltip title={label}>
                            <Box component="span" sx={{ display: 'inline-flex' }}>
                              <Button
                                onClick={() => handleRemoveMandByType(target, countKey)}
                                disabled={count === 0}
                                sx={{ px: 1, minWidth: 36 }}
                                aria-label={`Decrease ${label}`}
                              >
                                <MinusOutlined style={{ fontSize: '0.8rem' }} />
                              </Button>
                            </Box>
                          </Tooltip>
                          <Button
                            onClick={() => handleAddMand(target, countKey)}
                            sx={{ minWidth: 48, px: 0.5, fontWeight: 700, fontSize: '0.95rem' }}
                            aria-label={`${label} count: ${count}. Tap to add.`}
                          >
                            {count}
                          </Button>
                          <Tooltip title={label}>
                            <Button
                              onClick={() => handleAddMand(target, countKey)}
                              sx={{ px: 1, minWidth: 36 }}
                              aria-label={`Increase ${label}`}
                            >
                              <PlusOutlined style={{ fontSize: '0.8rem' }} />
                            </Button>
                          </Tooltip>
                        </ButtonGroup>
                      </Stack>
                    );
                  };

                  return (
                    <Grid size={{ xs: 12, sm: 6, md: 6, lg: 4 }} key={key}>
                      <Paper
                        sx={{
                          p: 2,
                          border: '1px solid',
                          borderColor: 'grey.300',
                          borderRadius: 2,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1.5,
                          transition: 'box-shadow 0.2s ease-in-out',
                          '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }
                        }}
                      >
                        {/* Card title row: material/item name is centered like a
                              card header, with the running total chip and download
                              icon flanking it so the name reads as the focal point. */}
                        <Stack
                          direction="row"
                          alignItems="center"
                          sx={{ minHeight: 36, borderBottom: '1px solid', borderColor: 'divider', pb: 1 }}
                        >
                          <Box sx={{ width: 32, display: 'flex', justifyContent: 'flex-start' }}>
                            {totalCount > 0 && (
                              <Chip
                                label={totalCount}
                                size="small"
                                color="primary"
                                sx={{ height: 22, minWidth: 22, fontSize: '0.75rem' }}
                              />
                            )}
                          </Box>
                          <Stack sx={{ flex: 1, minWidth: 0, alignItems: 'center', px: 1 }}>
                            {/* Show the TARGET as the card title (not the material/
                                  image name). The materials themselves are still
                                  available to pick in the "Download All" dialog. */}
                            <Typography
                              variant="subtitle1"
                              fontWeight={600}
                              textAlign="center"
                              color="text.primary"
                              sx={{ wordBreak: 'break-word', lineHeight: 1.4, fontSize: '0.95rem' }}
                              title={row.targetName}
                            >
                              {row.targetName || '-'}
                            </Typography>
                          </Stack>
                          {/* Right spacer balances the left count-chip box so
                                the material name stays centered. Per-card download
                                removed — exporting is handled via "Download All". */}
                          <Box sx={{ width: 32 }} />
                        </Stack>
                        <Stack spacing={1.25} sx={{ width: '100%' }}>
                          {renderCounterRow('prompted')}
                          {renderCounterRow('unprompted')}
                        </Stack>
                      </Paper>
                    </Grid>
                  );
                })}
                {/* Custom (ad-hoc) mand cards, then the always-present Add card. */}
                {customMands.map((m) => renderCustomMandCard(m))}
                {renderAddMandCard()}
              </Grid>
            </Box>
          </MainCard>

          {/* Recent Mands — temporarily hidden per UX feedback (too distracting).
              The underlying mandOccurrences state and handlers are still in
              place; uncomment to restore.
          <MainCard>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h5">
                {intl.formatMessage({ id: 'form.dailyMand.recentMands' }, { count: mandOccurrences.length })}
              </Typography>
            </Stack>

            {mandOccurrences.length === 0 ? (
              <Stack alignItems="center" spacing={2} sx={{ py: 6 }}>
                <Typography variant="h6" color="text.secondary">
                  {intl.formatMessage({ id: 'form.dailyMand.noMandsRecorded' })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {intl.formatMessage({ id: 'form.dailyMand.clickToRecord' })}
                </Typography>
              </Stack>
            ) : (
              <Stack spacing={1.5}>
                {mandOccurrences.map((mand) => {
                  const typeColor = mand.type === 'prompted' ? 'warning' : 'success';
                  return (
                    <Paper key={mand.id} sx={{ p: 2, border: 1, borderColor: 'divider' }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                        <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1 }}>
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRadius: 1,
                              bgcolor: `${typeColor}.lighter`,
                              color: `${typeColor}.main`,
                              fontWeight: 600
                            }}
                          >
                            {mand.type === 'prompted' ? <TeamOutlined /> : <UserOutlined />}
                          </Box>
                          <Stack sx={{ flex: 1 }}>
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                              <Typography variant="subtitle2" fontWeight={600}>
                                {mand.materialName || '-'}
                              </Typography>
                              {mand.targetName && (
                                <Chip label={mand.targetName} size="small" color="default" variant="outlined" />
                              )}
                              <Chip
                                label={
                                  mand.type === 'prompted'
                                    ? intl.formatMessage({ id: 'common.prompted' })
                                    : intl.formatMessage({ id: 'common.independent' })
                                }
                                size="small"
                                color={typeColor}
                                variant="filled"
                              />
                            </Stack>
                            <Typography variant="caption" color="text.secondary">
                              <ClockCircleOutlined style={{ fontSize: '0.9rem', marginRight: 4 }} />
                              {mand.time}
                            </Typography>
                            <TextField
                              size="small"
                              fullWidth
                              placeholder={intl.formatMessage({ id: 'form.dailyMand.addNotes' })}
                              value={mand.notes}
                              onChange={(e) => handleUpdateNotes(mand.id, e.target.value)}
                              sx={{ mt: 1 }}
                            />
                          </Stack>
                        </Stack>
                        <IconButton size="small" color="error" onClick={() => handleRemoveMand(mand.id)}>
                          <DeleteOutlined />
                        </IconButton>
                      </Stack>
                    </Paper>
                  );
                })}
              </Stack>
            )}
          </MainCard>
          */}

          {/* Session Notes - Offline Mode Only */}
          {isOfflineMode && (
            <MainCard>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                {intl.formatMessage({ id: 'form.label.sessionNotes' })}
              </Typography>
              <SpeechTextField
                fullWidth
                multiline
                rows={3}
                placeholder={intl.formatMessage({ id: 'form.label.notesPlaceholder' })}
                value={sessionNotes}
                onChange={(e) => setSessionNotes(e.target.value)}
              />
            </MainCard>
          )}

          {/* Download Materials Dialog — select which material images to export,
              then render them into a single PDF (one image per page). */}
          <Dialog open={downloadDialogOpen} onClose={handleCloseDownloadDialog} maxWidth="md" fullWidth>
            <DialogTitle>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                <Typography variant="h5">{intl.formatMessage({ id: 'form.dailyMand.downloadDialogTitle' })}</Typography>
                <FormControlLabel
                  sx={{ mr: 0 }}
                  control={
                    <Checkbox
                      checked={allDownloadSelected}
                      indeterminate={!allDownloadSelected && someDownloadSelected}
                      onChange={toggleSelectAllDownloads}
                      disabled={downloadableRows.length === 0 || generatingPdf}
                    />
                  }
                  label={intl.formatMessage({ id: 'form.dailyMand.selectAll' })}
                />
              </Stack>
            </DialogTitle>
            <DialogContent dividers>
              {downloadableRows.length === 0 ? (
                <Alert severity="info">{intl.formatMessage({ id: 'form.dailyMand.noDownloadableMaterials' })}</Alert>
              ) : (
                <>
                  {/* Page layout: images per page (1×1 / 2×2 / 3×3). */}
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      {intl.formatMessage({ id: 'form.dailyMand.pageLayout', defaultMessage: 'Images per page' })}
                    </Typography>
                    <ToggleButtonGroup
                      size="small"
                      exclusive
                      value={pdfLayout}
                      onChange={(_e, v) => v && setPdfLayout(v)}
                      disabled={generatingPdf || (showTitle && titlePosition === 'beside')}
                    >
                      <ToggleButton value={1} disabled={showTitle && titlePosition === 'foldable'}>
                        1×1
                      </ToggleButton>
                      <ToggleButton value={2}>2×2</ToggleButton>
                      <ToggleButton value={3} disabled={showTitle && titlePosition === 'foldable'}>
                        3×3
                      </ToggleButton>
                    </ToggleButtonGroup>
                    <Typography variant="caption" color="text.secondary">
                      {intl.formatMessage(
                        { id: 'form.dailyMand.perPageHint', defaultMessage: '{n} per page' },
                        { n: showTitle && titlePosition === 'beside' ? BESIDE_ROWS : pdfLayout * pdfLayout }
                      )}
                    </Typography>
                  </Stack>

                  {/* Title options: whether to print the material name, where, and how big. */}
                  <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                    <FormControlLabel
                      sx={{ mr: 0 }}
                      control={<Checkbox checked={showTitle} onChange={(e) => setShowTitle(e.target.checked)} disabled={generatingPdf} />}
                      label={intl.formatMessage({ id: 'form.dailyMand.showTitle', defaultMessage: 'Show title' })}
                    />
                    {showTitle && (
                      <>
                        <FormControl size="small" sx={{ minWidth: 210 }} disabled={generatingPdf}>
                          <InputLabel id="title-position-label">
                            {intl.formatMessage({ id: 'form.dailyMand.titlePosition', defaultMessage: 'Title position' })}
                          </InputLabel>
                          <Select
                            labelId="title-position-label"
                            label={intl.formatMessage({ id: 'form.dailyMand.titlePosition', defaultMessage: 'Title position' })}
                            value={titlePosition}
                            onChange={(e) => {
                              const v = e.target.value;
                              setTitlePosition(v);
                              // "Below (Foldable)" supports only the 2×2 layout
                              if (v === 'foldable') setPdfLayout(2);
                            }}
                          >
                            <MenuItem value="below">
                              {intl.formatMessage({ id: 'form.dailyMand.titleBelow', defaultMessage: 'Below Image' })}
                            </MenuItem>
                            {/* "Below Image (Foldable)" is hidden for now (logic kept for later). */}
                            <MenuItem value="beside">
                              {intl.formatMessage({ id: 'form.dailyMand.titleBeside', defaultMessage: 'Beside Image (Foldable)' })}
                            </MenuItem>
                          </Select>
                        </FormControl>
                        <Tooltip
                          title={intl.formatMessage({
                            id: 'form.dailyMand.titleFoldableHint',
                            defaultMessage:
                              '"Below Image (Foldable)" splits the tile in half — image on top (full width), title in the bottom half below the dashed fold line — so the card can be folded. Only the 2×2 layout is available with this option.'
                          })}
                        >
                          <InfoCircleOutlined style={{ color: '#888', cursor: 'help', fontSize: 16 }} />
                        </Tooltip>
                        <FormControl size="small" sx={{ minWidth: 160 }} disabled={generatingPdf}>
                          <InputLabel id="title-fontsize-label">
                            {intl.formatMessage({ id: 'form.dailyMand.titleFontSize', defaultMessage: 'Title font size' })}
                          </InputLabel>
                          <Select
                            labelId="title-fontsize-label"
                            label={intl.formatMessage({ id: 'form.dailyMand.titleFontSize', defaultMessage: 'Title font size' })}
                            value={titleFontSize}
                            onChange={(e) => setTitleFontSize(e.target.value)}
                          >
                            <MenuItem value="large">
                              {intl.formatMessage({ id: 'form.dailyMand.fontLarge', defaultMessage: 'Large' })}
                            </MenuItem>
                            <MenuItem value="medium">
                              {intl.formatMessage({ id: 'form.dailyMand.fontMedium', defaultMessage: 'Medium' })}
                            </MenuItem>
                            <MenuItem value="small">
                              {intl.formatMessage({ id: 'form.dailyMand.fontSmall', defaultMessage: 'Small' })}
                            </MenuItem>
                          </Select>
                        </FormControl>
                      </>
                    )}
                  </Stack>

                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    {intl.formatMessage({
                      id: 'form.dailyMand.reorderHint',
                      defaultMessage: 'Drag images to reorder — the PDF follows this order. Click to select/deselect.'
                    })}
                  </Typography>
                  <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handleDownloadDragEnd}>
                    <SortableContext items={downloadOrder} strategy={rectSortingStrategy}>
                      <Grid container spacing={2}>
                        {orderedDownloadRows.map((row) => (
                          <SortableMaterialCard
                            key={row.key}
                            row={row}
                            checked={selectedDownloadKeys.has(row.key)}
                            onToggle={toggleDownloadKey}
                            onEdit={openEditMaterial}
                            disabled={generatingPdf}
                            showTarget={isAllTargetsMode}
                          />
                        ))}
                      </Grid>
                    </SortableContext>
                  </DndContext>
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDownloadDialog} disabled={generatingPdf}>
                {intl.formatMessage({ id: 'common.cancel' })}
              </Button>
              <Button
                variant="outlined"
                startIcon={<EyeOutlined />}
                onClick={handlePreviewPdf}
                disabled={generatingPdf || selectedDownloadKeys.size === 0}
              >
                {intl.formatMessage({ id: 'form.dailyMand.preview', defaultMessage: 'Preview' })}
              </Button>
              <Button
                variant="contained"
                startIcon={generatingPdf ? <CircularProgress size={16} color="inherit" /> : <DownloadOutlined />}
                onClick={handleDownloadSelectedAsPdf}
                disabled={generatingPdf || selectedDownloadKeys.size === 0}
              >
                {generatingPdf
                  ? intl.formatMessage({ id: 'form.dailyMand.generatingPdf' })
                  : intl.formatMessage({ id: 'form.dailyMand.downloadSelectedCount' }, { count: selectedDownloadKeys.size })}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Edit Material — description & tags only (image and name are not editable here). */}
          <Dialog open={Boolean(editMatRow)} onClose={closeEditMaterial} maxWidth="sm" fullWidth>
            <DialogTitle>{intl.formatMessage({ id: 'form.dailyMand.editMaterialTitle', defaultMessage: 'Edit material' })}</DialogTitle>
            <DialogContent dividers>
              {editMatRow && (
                <Stack spacing={2.5} sx={{ mt: 0.5, py: 1 }}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box
                      component="img"
                      src={editMatRow.material.url}
                      alt=""
                      sx={{
                        width: 56,
                        height: 56,
                        objectFit: 'contain',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        flexShrink: 0
                      }}
                    />
                    <Typography variant="subtitle1" fontWeight={600} noWrap>
                      {editMatRow.materialName || editMatRow.material.name}
                    </Typography>
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    {intl.formatMessage({
                      id: 'form.dailyMand.editMaterialNote',
                      defaultMessage: "The image and name can't be changed here — edit the description and tags only."
                    })}
                  </Typography>
                  <TextField
                    label={intl.formatMessage({ id: 'materials.description', defaultMessage: 'Description' })}
                    fullWidth
                    multiline
                    rows={4}
                    value={editDesc}
                    onChange={(e) => setEditDesc(e.target.value)}
                    disabled={savingMeta}
                  />
                  <Autocomplete
                    multiple
                    freeSolo
                    options={[]}
                    value={editTags}
                    onChange={(_e, v) => setEditTags(normalizeMetaTags(v))}
                    disabled={savingMeta}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => {
                        const { key, ...chipProps } = getTagProps({ index });
                        return <Chip key={key} label={option} size="small" {...chipProps} />;
                      })
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={intl.formatMessage({ id: 'materials.tags', defaultMessage: 'Tags' })}
                        placeholder={
                          editTags.length >= 5 ? '' : intl.formatMessage({ id: 'materials.tagsPlaceholder', defaultMessage: 'Add a tag' })
                        }
                        helperText={intl.formatMessage({
                          id: 'materials.tagsHelper',
                          defaultMessage: 'Press Enter to add a tag. Up to 5; tags can be phrases.'
                        })}
                      />
                    )}
                  />
                </Stack>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={closeEditMaterial} disabled={savingMeta}>
                {intl.formatMessage({ id: 'common.cancel' })}
              </Button>
              <Button
                variant="contained"
                onClick={saveEditMaterial}
                disabled={savingMeta}
                startIcon={savingMeta ? <CircularProgress size={16} color="inherit" /> : null}
              >
                {intl.formatMessage({ id: 'common.save', defaultMessage: 'Save' })}
              </Button>
            </DialogActions>
          </Dialog>

          {/* PDF Preview Dialog — shows the generated PDF (with the chosen layout)
              in an iframe before the therapist commits to downloading it. */}
          <Dialog
            open={previewOpen}
            onClose={closePreview}
            maxWidth="lg"
            fullWidth
            PaperProps={{ sx: { height: '92vh', display: 'flex', flexDirection: 'column' } }}
          >
            <DialogTitle>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                <Typography variant="h5">
                  {intl.formatMessage({ id: 'form.dailyMand.previewTitle', defaultMessage: 'Preview' })}
                  {isTouch && previewPages.pages.length > 1 && ` — ${previewPages.pages.length} pages (scroll ↓)`}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {intl.formatMessage(
                    { id: 'form.dailyMand.perPageHint', defaultMessage: '{n} per page' },
                    { n: showTitle && titlePosition === 'beside' ? BESIDE_ROWS : pdfLayout * pdfLayout }
                  )}
                </Typography>
              </Stack>
            </DialogTitle>
            <DialogContent dividers sx={{ p: 0, flex: 1, minHeight: 0, overflow: 'hidden' }}>
              {isTouch ? (
                /* Scrollable HTML page-preview for touch devices (all cards visible). */
                <Box
                  sx={{ height: '100%', overflowY: 'auto', WebkitOverflowScrolling: 'touch', bgcolor: 'grey.100', p: { xs: 1.5, sm: 3 } }}
                >
                  {previewPages.pages.map((pageRows, pIdx) => {
                    const { cols, besideMode } = previewPages;
                    const titlePx = { large: 15, medium: 12, small: 10 }[titleFontSize] || 12;
                    return (
                      <Box key={pIdx}>
                        <Typography
                          variant="caption"
                          sx={{ display: 'block', textAlign: 'center', color: 'text.secondary', fontWeight: 600, mb: 0.5 }}
                        >
                          {intl.formatMessage(
                            { id: 'form.dailyMand.previewPageOf', defaultMessage: 'Page {n} of {total}' },
                            { n: pIdx + 1, total: previewPages.pages.length }
                          )}
                        </Typography>
                        <Box
                          sx={{
                            maxWidth: 620,
                            mx: 'auto',
                            mb: 3,
                            bgcolor: '#fff',
                            border: '1px solid',
                            borderColor: 'grey.500',
                            boxShadow: 1,
                            p: 1.5,
                            display: 'grid',
                            gridTemplateColumns: `repeat(${cols}, 1fr)`
                          }}
                        >
                          {pageRows.map((r) => (
                            <Box
                              key={r.key}
                              sx={{
                                // Aspect ratio on the CELL (its width is definite from the grid
                                // column) — reliable on iOS. Ratios mirror an A4 cell: N×N grid →
                                // 595/842; foldable → 595/210.
                                aspectRatio: besideMode ? '595 / 210' : '595 / 842',
                                border: '1px dashed',
                                borderColor: 'grey.400',
                                minHeight: 0,
                                minWidth: 0,
                                p: 0.5,
                                display: 'flex',
                                flexDirection: besideMode ? 'row' : 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 0.5
                              }}
                            >
                              <Box
                                component="img"
                                src={r.material.url}
                                alt={r.materialName || r.material.name}
                                sx={{
                                  width: besideMode ? '50%' : '100%',
                                  height: besideMode ? '100%' : 'auto',
                                  flexGrow: besideMode ? 0 : 1,
                                  flexShrink: 1,
                                  minHeight: 0,
                                  objectFit: 'contain'
                                }}
                              />
                              {showTitle && (
                                <Typography
                                  sx={{
                                    width: besideMode ? '50%' : '100%',
                                    flexShrink: 0,
                                    textAlign: 'center',
                                    fontSize: `${titlePx}px`,
                                    lineHeight: 1.2,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical'
                                  }}
                                >
                                  {r.materialName || r.material.name}
                                </Typography>
                              )}
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              ) : (
                previewUrl && (
                  <Box
                    component="iframe"
                    title="pdf-preview"
                    src={previewUrl}
                    sx={{ width: '100%', height: '100%', display: 'block', border: 'none' }}
                  />
                )
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={closePreview}>{intl.formatMessage({ id: 'common.cancel' })}</Button>
              <Button variant="contained" startIcon={<DownloadOutlined />} onClick={handleDownloadFromPreview}>
                {intl.formatMessage({ id: 'form.dailyMand.download', defaultMessage: 'Download' })}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Image Popup Dialog - Only Image */}
          <Dialog
            open={imagePopup.open}
            onClose={handleCloseImagePopup}
            maxWidth={false}
            PaperProps={{
              sx: {
                bgcolor: 'transparent',
                boxShadow: 'none',
                overflow: 'visible',
                m: 0
              }
            }}
          >
            {imagePopup.item && (
              <Box
                component="img"
                src={imagePopup.item.image}
                alt={imagePopup.item.name}
                sx={{
                  width: '75vw',
                  height: '75vh',
                  objectFit: 'contain',
                  borderRadius: 2,
                  display: 'block'
                }}
              />
            )}
          </Dialog>
        </Stack>
      </Box>
    );
  }

  // Grid View
  return (
    <MainCard>
      {/* Network Lost Banner - shown when not in explicit offline mode but network is down */}
      {!isOfflineMode && !isOnline && (
        <Alert severity="info" icon={<DisconnectOutlined />} sx={{ mb: 2 }}>
          <Typography variant="body2">{intl.formatMessage({ id: 'form.msg.offlineNote' })}</Typography>
        </Alert>
      )}

      <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tab label={intl.formatMessage({ id: 'form.tab.dataEntry' })} />
        <Tab label={intl.formatMessage({ id: 'form.tab.historicalData' })} />
      </Tabs>

      <Box sx={{ pt: 3 }}>
        {/* Tab 0: Data Entry */}
        {tabValue === 0 && (
          <>
            {/* Mands Overview — same cards as individual view */}
            <Typography variant="h5" sx={{ mb: 2 }}>
              {intl.formatMessage({ id: 'form.dailyMand.mandsOverview' })}
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <MainCard>
                  <Stack spacing={0.5}>
                    <Typography variant="h3" fontWeight={700} color="warning.main">
                      {getDisplayPromptedTotal()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {intl.formatMessage({ id: 'form.dailyMand.promptedMands' })}
                    </Typography>
                  </Stack>
                </MainCard>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <MainCard>
                  <Stack spacing={0.5}>
                    <Typography variant="h3" fontWeight={700} color="success.main">
                      {getDisplayIndependentTotal()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {intl.formatMessage({ id: 'form.dailyMand.independentMands' })}
                    </Typography>
                  </Stack>
                </MainCard>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <MainCard>
                  <Stack spacing={0.5}>
                    <Typography variant="h3" fontWeight={700} color="info.main">
                      {getDisplayTotalMands()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {intl.formatMessage({ id: 'form.dailyMand.totalMands' })}
                    </Typography>
                  </Stack>
                </MainCard>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <MainCard>
                  <Stack spacing={0.5}>
                    <Typography variant="h3" fontWeight={700} color="primary.main">
                      {getRpm()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {intl.formatMessage({ id: 'form.dailyMand.responsesPerMin' })}
                    </Typography>
                  </Stack>
                </MainCard>
              </Grid>
            </Grid>

            {/* Mand Frequency Table — one row per target, matching the Quick
                Add cards. Counts are the per-target prompted/independent
                tallies. */}
            <MainCard>
              <Typography variant="h5" sx={{ mb: 2 }}>
                {intl.formatMessage({ id: 'form.dailyMand.mandFrequencyByItem' })}
              </Typography>
              {flatRows.length === 0 ? (
                <Alert severity="info">{intl.formatMessage({ id: 'form.dailyMand.noMaterialsConfigured' })}</Alert>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>#</TableCell>
                        <TableCell>{intl.formatMessage({ id: 'form.label.target' })}</TableCell>
                        <TableCell align="center" width={120}>
                          {intl.formatMessage({ id: 'common.prompted' })}
                        </TableCell>
                        <TableCell align="center" width={120}>
                          {intl.formatMessage({ id: 'common.independent' })}
                        </TableCell>
                        <TableCell align="center" width={100}>
                          {intl.formatMessage({ id: 'form.dailyMand.total' })}
                        </TableCell>
                        <TableCell align="center" width={60} />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {flatRows.map((row, index) => {
                        const promptedCount = gridPromptedCounts[row.key] ?? 0;
                        const independentCount = gridIndependentCounts[row.key] ?? 0;
                        const totalCount = promptedCount + independentCount;

                        return (
                          <TableRow key={row.key} hover>
                            <TableCell>{index + 1}</TableCell>
                            <TableCell>
                              <Typography variant="body2">{row.targetName}</Typography>
                            </TableCell>
                            <TableCell align="center">
                              <TextField
                                type="number"
                                size="small"
                                value={promptedCount}
                                onChange={(e) => {
                                  const value = Math.max(0, parseInt(e.target.value, 10) || 0);
                                  setGridPromptedCounts({ ...gridPromptedCounts, [row.key]: value });
                                }}
                                inputProps={{
                                  min: 0,
                                  style: { textAlign: 'center' }
                                }}
                                sx={{ width: 80 }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <TextField
                                type="number"
                                size="small"
                                value={independentCount}
                                onChange={(e) => {
                                  const value = Math.max(0, parseInt(e.target.value, 10) || 0);
                                  setGridIndependentCounts({ ...gridIndependentCounts, [row.key]: value });
                                }}
                                inputProps={{
                                  min: 0,
                                  style: { textAlign: 'center' }
                                }}
                                sx={{ width: 80 }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={totalCount}
                                size="small"
                                color={totalCount > 0 ? 'primary' : 'default'}
                                variant={totalCount > 0 ? 'filled' : 'outlined'}
                              />
                            </TableCell>
                            {/* Actions column — empty for target-driven rows (no delete). */}
                            <TableCell align="center" />
                          </TableRow>
                        );
                      })}
                      {/* Custom (ad-hoc) mand rows — free-text name, counters disabled
                          until named, total in the badge, delete on custom rows only. */}
                      {customMands.map((m, i) => {
                        const disabled = !(m.name || '').trim();
                        const total = customMandTotal(m);
                        return (
                          <TableRow
                            key={m.id}
                            hover
                            sx={{
                              '& > td': { borderLeft: 'none' },
                              '& > td:first-of-type': { borderLeft: `4px solid ${customMandBorder}` },
                              bgcolor: customMandTint
                            }}
                          >
                            <TableCell>{flatRows.length + i + 1}</TableCell>
                            <TableCell>
                              <TextField
                                size="small"
                                fullWidth
                                value={m.name}
                                onChange={(e) => updateCustomMandName(m.id, e.target.value)}
                                placeholder={intl.formatMessage({ id: 'form.dailyMand.customMandNamePlaceholder' })}
                                inputProps={{ 'aria-label': 'Custom mand name' }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <TextField
                                type="number"
                                size="small"
                                value={Number(m.prompted) || 0}
                                disabled={disabled}
                                onChange={(e) => setCustomMandCount(m.id, 'prompted', e.target.value)}
                                inputProps={{ min: 0, style: { textAlign: 'center' } }}
                                sx={{ width: 80 }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <TextField
                                type="number"
                                size="small"
                                value={Number(m.independent) || 0}
                                disabled={disabled}
                                onChange={(e) => setCustomMandCount(m.id, 'independent', e.target.value)}
                                inputProps={{ min: 0, style: { textAlign: 'center' } }}
                                sx={{ width: 80 }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={total}
                                size="small"
                                color={total > 0 ? 'primary' : 'default'}
                                variant={total > 0 ? 'filled' : 'outlined'}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip title={intl.formatMessage({ id: 'common.delete' })}>
                                <IconButton size="small" onClick={() => removeCustomMand(m.id)} aria-label="Delete custom mand">
                                  <DeleteOutlined style={{ fontSize: '1rem' }} />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {/* Add mand — stays at the bottom so any number can be added. */}
                      <TableRow>
                        <TableCell colSpan={6} sx={{ borderBottom: 'none' }}>
                          <Button size="small" startIcon={<PlusOutlined />} onClick={addCustomMand} sx={{ textTransform: 'none' }}>
                            {intl.formatMessage({ id: 'form.dailyMand.addMand' })}
                          </Button>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </MainCard>

            {/* <Stack direction="row" justifyContent="flex-end" spacing={2} sx={{ mt: 3 }}>
              <Button variant="outlined" startIcon={<SaveOutlined />} onClick={syncToHistoricalData}>
                Save Draft
              </Button>
              <Button variant="contained" color="success" startIcon={<CheckOutlined />} onClick={syncToHistoricalData}>
                Submit Data
              </Button>
            </Stack> */}
          </>
        )}

        {/* Tab 1: Historical Data */}
        {tabValue === 1 && (
          <>
            {/* Search, Date Filters and Export */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3, alignItems: 'center' }}>
              <DebouncedInput
                value={globalFilter ?? ''}
                onFilterChange={(value) => setGlobalFilter(String(value))}
                placeholder={intl.formatMessage({ id: 'form.dailyMand.searchPlaceholder' })}
                sx={{ flexGrow: 1 }}
              />

              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label={intl.formatMessage({ id: 'form.dailyMand.fromDate' })}
                  value={startDate}
                  onChange={(newValue) => setStartDate(newValue)}
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: { minWidth: 180 }
                    }
                  }}
                />
                <DatePicker
                  label={intl.formatMessage({ id: 'form.dailyMand.toDate' })}
                  value={endDate}
                  onChange={(newValue) => setEndDate(newValue)}
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: { minWidth: 180 }
                    }
                  }}
                />
              </LocalizationProvider>

              <CSVExport data={historicalData} filename={`daily-mand-history-${format(new Date(), 'yyyy-MM-dd')}.csv`} />
            </Stack>

            {/* Historical Data Table */}
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{intl.formatMessage({ id: 'form.label.date' })}</TableCell>
                    <TableCell>{intl.formatMessage({ id: 'form.label.target' })}</TableCell>
                    <TableCell align="center">{intl.formatMessage({ id: 'form.dailyMand.promptedMands' })}</TableCell>
                    <TableCell align="center">{intl.formatMessage({ id: 'form.dailyMand.independentMands' })}</TableCell>
                    <TableCell align="center">{intl.formatMessage({ id: 'form.dailyMand.itemsUsed' })}</TableCell>
                    <TableCell align="center">{intl.formatMessage({ id: 'form.dailyMand.rpm' })}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        {/* Nothing ever recorded reads differently from filters that
                            matched nothing — the first is a fact about the child, the
                            second a fact about the filters. */}
                        {historyIsEmpty ? (
                          <HistoryEmptyState />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            {intl.formatMessage({ id: 'form.dailyMand.noRecordsFound' })}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedData.map((row, index) => (
                      <TableRow key={index} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {format(new Date(row.date), 'MMM dd, yyyy')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {typeof row.target === 'object' && row.target !== null
                              ? row.target.name || row.target.description || ''
                              : row.target}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip label={row.promptedMands} size="small" color="warning" />
                        </TableCell>
                        <TableCell align="center">
                          <Chip label={row.independentMands} size="small" color="success" />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{row.itemsUsed}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight={600}>
                            {row.rpm}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <Stack sx={{ p: 2 }}>
              <TablePagination
                getPageCount={() => Math.ceil(filteredHistoricalData.length / pagination.pageSize)}
                setPageIndex={(pageIndex) => setPagination({ ...pagination, pageIndex })}
                setPageSize={(pageSize) => setPagination({ pageIndex: 0, pageSize })}
                getState={() => ({ pagination })}
              />
            </Stack>
          </>
        )}
      </Box>

      {/* Image Popup Dialog - Only Image */}
      <Dialog
        open={imagePopup.open}
        onClose={handleCloseImagePopup}
        maxWidth={false}
        PaperProps={{
          sx: {
            bgcolor: 'transparent',
            boxShadow: 'none',
            overflow: 'visible',
            m: 0
          }
        }}
      >
        {imagePopup.item && (
          <Box
            component="img"
            src={imagePopup.item.image}
            alt={imagePopup.item.name}
            sx={{
              width: '75vw',
              height: '75vh',
              objectFit: 'contain',
              borderRadius: 2,
              display: 'block'
            }}
          />
        )}
      </Dialog>
    </MainCard>
  );
};

DailyMandMode.propTypes = {
  viewMode: PropTypes.string,
  onDataChange: PropTypes.func,
  initialData: PropTypes.object,
  mandItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
      category: PropTypes.string.isRequired,
      image: PropTypes.string
    })
  ),
  targets: PropTypes.arrayOf(PropTypes.string),
  // Offline mode props
  isOfflineMode: PropTypes.bool,
  childId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  childName: PropTypes.string,
  therapistId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  therapistName: PropTypes.string,
  onSave: PropTypes.func,
  onBack: PropTypes.func
};

export default DailyMandMode;
